import {ArenaFiltersConfigurationMap, IArenaViewModel} from "./arena-view-model.interface";
import {
    ArenaGamesGridConfiguration,
    SwiperGamesListConfiguration
} from "../../../configurations/config-data-sections/arenas.configuration";
import { action, computed, observable, reaction, makeObservable } from "mobx";
import {IArenaToGamesListAdapter} from "./arena-to-games-list-adapter.interface";
import {IServiceFactoryExtended} from "../../../service-factory-extended.interface";
import {IGamesListModel} from "../lists/games-list-model.interface";
import React from "react";
import {ListFactory} from "../lists/list-factory";
import {Check} from "@sparkware/app-utils";
import {ListsSortOrderStorage} from "../lists-sort-order-storage";
import {IGamesListRenderer} from "../lists/games-list-renderer.interface";
import {BannerComponent} from "../../../../components/banner/banner.component";
import {GameInfo} from "../../../games/game-info";
import {IGamesFilterStrategy} from "../../../games-filter/strategies/games-filter-strategy.interface";

export type BannerConfig = {position: number; templateID: string; componentID: string; fallbackComponent: string, bannerAdminPlaceholderId: string};

export abstract class ArenaBaseModel implements IArenaToGamesListAdapter, IArenaViewModel {
    constructor(public readonly arenaName: string,
                public readonly services: IServiceFactoryExtended,
                private readonly _postLoginListsSortOrderStorage: ListsSortOrderStorage) {
        makeObservable(this);

        this._listFactory = new ListFactory(this);

        reaction(() => [this.services.user.isAuthorized, this.services.routing.currentRoute],
            async () => {
                await this._updateAIEngineSwipersSortOrder();
            }, {
                fireImmediately: true
            });
    }

    private _listFactory: ListFactory;
    abstract get maxSwipersToShow(): number;
    abstract get maxGamesToShowInList(): number;
    protected abstract _getSwipersConfigurations(): SwiperGamesListConfiguration[];
    protected abstract _getGridsConfigurations(): ArenaGamesGridConfiguration[];
    protected abstract _getPromotionalBannerConfig(): BannerConfig | null;
    abstract renderArena(): React.ReactElement | null;
    abstract filterGamesByArenaType(gameInfos: GameInfo[]): GameInfo[];
    abstract get filtersConfigurationMap(): ArenaFiltersConfigurationMap;
    abstract getGamesFilteringStrategy(): IGamesFilterStrategy;

    protected get useAdvancedFilters(): boolean {
        return this.services.configuration.servicesConfigs.Filters.useAdvancedFilters;
    }

    protected get _isUsingExternalAIForSwipersSorting(): boolean {
        return false;
    }

    protected _getSwiperIdToPutOnTop(): number | null {
        return null;
    }

    get aiEngine(): string {
        return "";
    }

    @observable.ref
    private _aiEngineListsSortOrder: Record<number, number> = null;

    @action.bound
    private _setAiEngineListsSortOrder(value: Record<number, number>): void {
        this._aiEngineListsSortOrder = value;
    }
    private async _updateAIEngineSwipersSortOrder(): Promise<void> {
        if(this._isUsingExternalAIForSwipersSorting) {
            return;
        }
        if(this._aiEngineListsSortOrder) {
            return;
        }

        if(!this.services.user.isAuthorized) {
            return;
        }

        if(this.services.routing.currentRoute.data?.arenaType === this.arenaName) {
            const listsIDs = this._getSwipersConfigurations().filter(sw => !Check.isNullOrUndefined(sw.listId))
                                                             .map(sw => sw.listId.toString());
            if(listsIDs.length === 0) {
                return;
            }

            this._setAiEngineListsSortOrder(await this.services.aiEngine.getCasinoRecommendedDynamicListsForPlayer(listsIDs));
        }
    }

    private _movePreferredSwiperOnTop(swipers: IGamesListModel[]){
        const listId = this._getSwiperIdToPutOnTop();
        if(Check.isNullOrUndefined(listId)) {
            return swipers;
        }
        const listIndex = swipers.findIndex((list) => list.listId === listId);
        if(listIndex > 0){
            swipers.splice(0, 0, swipers.splice(listIndex, 1)[0]);
        }
        return swipers;
    }


    protected _sortSwipersByAI(originalSwipers: IGamesListModel[]): IGamesListModel[] {
        const scores = this._aiEngineListsSortOrder;
        if(!scores) {
            return originalSwipers;
        }

        const notForcedInLayoutSortedSwipers = originalSwipers.filter(s => !s.forceInLayout)
                                                                     .sort((s1, s2) => (scores[s2.listId] || 0) - (scores[s1.listId] || 0));

        let sortedSwipersConfigs: IGamesListModel[] = [];
        originalSwipers.forEach(gameList => {
            if(gameList.forceInLayout){
                sortedSwipersConfigs.push(gameList)
            } else{
                let mappedGameList = notForcedInLayoutSortedSwipers.shift();
                if(mappedGameList) {
                    sortedSwipersConfigs.push(mappedGameList)
                }
            }
        });

        return sortedSwipersConfigs;
    }



    @computed
    public get allLists(): IGamesListModel[] {
        const result = this._createAllLists();
        this._sendGameListsAnalytics(result);
        return result;
    }

    @computed
    get allArenaGamesForFiltering(): GameInfo[] {
        let gameTypes: number[] = [];
        this._getGridsConfigurations().forEach(g => {
            gameTypes = [
                ...gameTypes,
                ...g.gamesId
            ]
        });

        //use Set here to make a list of distinct game types
        return [...new Set(gameTypes)].map(gameType => this.services.games.gamesMap[gameType]).filter(gameInfo => gameInfo);
    }


    get filteredGamesCount(): number {
        let gameTypes: number[] = [];

        for(let list of this.allLists) {
            gameTypes = [
                ...gameTypes,
                ...list.gameTypes
            ]
        }

        return [...new Set(gameTypes)].length;
    }

    get listsCount(): number {
        return this.allLists.length;
    }

    protected _createAllLists(): IGamesListModel[] {
        return this._appendGrids(
            this._limitTheNumberOfSwipers(
                this._saveSwipersPostLoginSortOrder(
                    this._sortSwipersByPostLoginOrder(
                        this._movePreferredSwiperOnTop(
                            this._sortSwipersByAI(
                                this._createSwipers()))))));
    }

    private _limitTheNumberOfSwipers(swipers: IGamesListModel[]): IGamesListModel[] {

        if(swipers.length <= this.maxSwipersToShow) {
            return swipers;
        }
        let result: IGamesListModel[] = [];

        let maxNumberOfSwipers = this.maxSwipersToShow;
        for(let i = 0; i < swipers.length; i++) {
            const list = swipers[i];
            if(maxNumberOfSwipers > 0 || list.forceInLayout) {
                result.push(list);
            }

            if(list.isVisible) {
                maxNumberOfSwipers--;
            }

        }

        return result;
    }

    renderList(listPosition: number): React.ReactElement | null {
        let position = listPosition;

        for(let list of this.allLists) {
            if(list.isVisible) {
                position--;
            }
            if(position === 0) {
                return this._renderListAndBanner(listPosition, list);
            }
        }

        return  null;
    }

    protected _renderListAndBanner(listPosition: number, list: IGamesListRenderer) {
        return (
            <React.Fragment key={list.listKey}>
                {list.render(listPosition)}
                {this._renderPromotionalBanner(listPosition)}
            </React.Fragment>
        )
    }

    getNextBatchSize(currentListPosition: number): number {
        if(currentListPosition > this.allLists.length) {
            return 0;
        }

        return 2;
    }

    protected _saveSwipersPostLoginSortOrder(swipers: IGamesListModel[]): IGamesListModel[] {
        if(this.services.user.isAuthorized) {
            this._postLoginListsSortOrderStorage.saveSortOrder(this.arenaName, swipers);
        }
        return swipers;
    }

    protected _sortSwipersByPostLoginOrder(swipers: IGamesListModel[]): IGamesListModel[] {
        if(this.services.user.isAuthorized) {
            return swipers;
        }

        return this._postLoginListsSortOrderStorage.sortLists(this.arenaName, swipers);
    }

    protected _createSwipers(): IGamesListModel[] {
        const configs = this._getSwipersConfigurations();
        const lists: IGamesListModel[] = [];

        configs.forEach((listConfig) => {
            lists.push(this._createSwiper(listConfig));
        });

        return lists;
    }

    protected _appendGrids(lists: IGamesListModel[]) {
        this._getGridsConfigurations().forEach(gridConfig => {
            lists.push(this._createGrid(gridConfig));
        });
        return lists;
    }

    protected _createGrid(gridConfig: ArenaGamesGridConfiguration) {
        return this._listFactory.createGrid(gridConfig);
    }

    protected _createSwiper(swiperConfig: SwiperGamesListConfiguration): IGamesListModel {
        return this._listFactory.createSwiper(swiperConfig);
    }

    protected _getBannerMinHeightInPixels(listPosition: number, bannerConfig: BannerConfig | null) {
        if(!bannerConfig) {
            return 0;
        }

        const shouldRenderBanner = listPosition === bannerConfig.position
            || (this.allLists.length < bannerConfig.position && listPosition === this.allLists.length);
        if(!shouldRenderBanner) {
            return 0;
        }

        if(this.services.mediaQuery.sAndBelow) {
            return 142;
        }

        return 223;
    }

    protected _renderPromotionalBanner(listPosition: number): React.ReactElement | null {
        const bannerConfig = this._getPromotionalBannerConfig();
        const bannerMinHeight = this._getBannerMinHeightInPixels(listPosition, bannerConfig);
        if(bannerMinHeight === 0) {
            return null;
        }

        return (
            <BannerComponent
                key={`${this.arenaName}_ARENA_PROMOTIONAL_BANNER_${listPosition}`}
                showPlaceholder={false}
                templateID={bannerConfig.templateID}
                componentID={bannerConfig.componentID}
                fallbackComponent={bannerConfig.fallbackComponent}
                minHeight={bannerMinHeight + 'px'}
                bannerAdminPlaceholderId={bannerConfig.bannerAdminPlaceholderId}
            />
        );
    }

    protected _sendGameListsAnalytics(lists: IGamesListModel[]): IGamesListModel[] {
        if(!this.services.user.isAuthorized) {
            return lists;
        }

        if(!this._isUsingExternalAIForSwipersSorting) {
            return lists;
        }

        const analyticsObject = {
            category: 'AI_ListsDisplayed',
            action: lists.map(list => list.title).join(','),
            label: "ML888"
        };

        this.services.analytics.sendAnalytics(analyticsObject.category, analyticsObject.action, analyticsObject.label);

        return lists;
    }
}